/**
 * useRoleConfig Hook
 * 
 * Fetches role configuration (hierarchy, colors, groupings) from the backend API.
 * This centralizes role metadata so we don't need hardcoded values scattered
 * throughout the frontend.
 * 
 * The hook caches the config and provides utility functions for role checks.
 * 
 * Usage:
 *   import { useRoleConfig, getRoleColor, getRoleLevel } from '../hooks/useRoleConfig';
 *   
 *   // In a component that needs dynamic role config
 *   const { roleConfig, loading, error } = useRoleConfig();
 *   
 *   // For simple color/level lookups (uses cached or fallback values)
 *   const color = getRoleColor('supervisor');
 */

import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../utils/apiClient';

// =====================================================
// Fallback defaults (used before API loads or on error)
// These should match the database values
// =====================================================

// V2 role hierarchy - maps role template IDs to hierarchy levels
const DEFAULT_ROLE_HIERARCHY = {
  // System
  superadmin: 0,
  // Upper Management
  admin: 1,
  construction_owner: 1,
  construction_director: 2,
  construction_area_manager: 3,
  accounting_controller: 5,
  operations_manager: 5,
  safety_officer_management: 5,
  quality_control_management: 5,
  // Manager
  pm: 2,
  construction_project_manager: 10,
  it_lead: 10,
  construction_scheduler: 15,
  sales: 15,
  // Lead
  cost_control: 2.5,
  tc: 3,
  supervisor: 4,
  construction_superintendent: 20,
  construction_general_foreman: 30,
  construction_foreman: 40,
  construction_technical_coordinator: 50,
  estimating_lead: 25,
  procurement_purchaser_lead: 25,
  // Staff
  qs: 5,
  viewer: 100
};

// V2 role colors - maps role template IDs to colors
const DEFAULT_ROLE_COLORS = {
  superadmin: '#dc3545',  // Red
  admin: '#007bff',       // Blue
  construction_owner: '#dc3545',
  construction_director: '#007bff',
  construction_area_manager: '#007bff',
  pm: '#28a745',          // Green
  construction_project_manager: '#28a745',
  cost_control: '#6610f2', // Indigo
  accounting_controller: '#007bff',
  tc: '#6f42c1',          // Purple
  construction_technical_coordinator: '#fd7e14',
  supervisor: '#20c997',  // Teal
  construction_superintendent: '#6f42c1',
  qs: '#fd7e14',          // Orange
  viewer: '#6c757d',      // Gray
  it_lead: '#6f42c1',
  operations_manager: '#007bff'
};

// V2 elevated roles (based on authority levels)
const DEFAULT_ELEVATED_ROLES = [
  'superadmin', 'admin', 'pm',
  'construction_owner', 'construction_director', 'construction_area_manager',
  'construction_project_manager', 'operations_manager', 'it_lead'
];

// V2 admin panel roles
const DEFAULT_ADMIN_PANEL_ROLES = [
  'superadmin', 'admin',
  'construction_owner', 'it_lead', 'operations_manager'
];

// V2 user management roles
const DEFAULT_USER_MANAGEMENT_ROLES = [
  'superadmin', 'admin',
  'construction_owner', 'it_lead', 'operations_manager', 'office_manager'
];

// =====================================================
// Cached config (shared across all hook instances)
// =====================================================

let cachedConfig = null;
let cachePromise = null;

// =====================================================
// Standalone utility functions (use cached or fallback)
// =====================================================

/**
 * Get the color for a role name
 * Uses cached config if available, otherwise falls back to defaults
 * @param {string} roleName - Role name
 * @returns {string} - Hex color code
 */
export const getRoleColor = (roleName) => {
  const name = roleName?.toLowerCase();
  if (cachedConfig?.roleColors?.[name]) {
    return cachedConfig.roleColors[name];
  }
  return DEFAULT_ROLE_COLORS[name] || DEFAULT_ROLE_COLORS.viewer;
};

/**
 * Get the hierarchy level for a role name (lower = more privileged)
 * Uses cached config if available, otherwise falls back to defaults
 * @param {string} roleName - Role name
 * @returns {number} - Hierarchy level
 */
export const getRoleLevel = (roleName) => {
  const name = roleName?.toLowerCase();
  if (cachedConfig?.roleHierarchy?.[name] !== undefined) {
    return cachedConfig.roleHierarchy[name];
  }
  return DEFAULT_ROLE_HIERARCHY[name] ?? 999;
};

/**
 * Check if a role is elevated (can perform privileged operations)
 * @param {string} roleName - Role name
 * @returns {boolean}
 */
export const isElevatedRole = (roleName) => {
  const name = roleName?.toLowerCase();
  const elevatedRoles = cachedConfig?.elevatedRoles || DEFAULT_ELEVATED_ROLES;
  return elevatedRoles.includes(name);
};

/**
 * Check if a role can access the admin panel
 * @param {string} roleName - Role name
 * @returns {boolean}
 */
export const canAccessAdminPanel = (roleName) => {
  const name = roleName?.toLowerCase();
  const adminRoles = cachedConfig?.adminPanelRoles || DEFAULT_ADMIN_PANEL_ROLES;
  return adminRoles.includes(name);
};

/**
 * Check if a role can manage users
 * @param {string} roleName - Role name
 * @returns {boolean}
 */
export const canManageUsers = (roleName) => {
  const name = roleName?.toLowerCase();
  const managementRoles = cachedConfig?.userManagementRoles || DEFAULT_USER_MANAGEMENT_ROLES;
  return managementRoles.includes(name);
};

/**
 * Get all roles sorted by hierarchy level
 * @returns {Array} - Array of role objects
 */
export const getSortedRoles = () => {
  if (cachedConfig?.roles) {
    return [...cachedConfig.roles].sort((a, b) => 
      (a.hierarchy_level ?? 999) - (b.hierarchy_level ?? 999)
    );
  }
  // Return defaults as role-like objects
  return Object.entries(DEFAULT_ROLE_HIERARCHY)
    .sort((a, b) => a[1] - b[1])
    .map(([name, level]) => ({
      name,
      hierarchy_level: level,
      color: DEFAULT_ROLE_COLORS[name]
    }));
};

// =====================================================
// Export constants for backward compatibility
// =====================================================

export const ROLE_HIERARCHY = DEFAULT_ROLE_HIERARCHY;
export const ROLE_COLORS = DEFAULT_ROLE_COLORS;
export const ELEVATED_ROLES = DEFAULT_ELEVATED_ROLES;
export const ADMIN_PANEL_ROLES = DEFAULT_ADMIN_PANEL_ROLES;
export const USER_MANAGEMENT_ROLES = DEFAULT_USER_MANAGEMENT_ROLES;

// =====================================================
// Main Hook
// =====================================================

/**
 * useRoleConfig hook - fetches and caches role configuration from API
 * 
 * @returns {Object} - { roleConfig, loading, error, refetch }
 */
export const useRoleConfig = () => {
  const { instance, accounts } = useMsal();
  const [roleConfig, setRoleConfig] = useState(cachedConfig);
  const [loading, setLoading] = useState(!cachedConfig);
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async (force = false) => {
    // If already cached and not forcing, use cache
    if (cachedConfig && !force) {
      setRoleConfig(cachedConfig);
      setLoading(false);
      return cachedConfig;
    }

    // If a fetch is already in progress, wait for it
    if (cachePromise && !force) {
      try {
        const config = await cachePromise;
        setRoleConfig(config);
        setLoading(false);
        return config;
      } catch (err) {
        setError(err.message);
        setLoading(false);
        return null;
      }
    }

    // Start new fetch
    setLoading(true);
    setError(null);

    cachePromise = (async () => {
      try {
        const response = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          '/auth/role-config'
        );

        if (response.data.success) {
          cachedConfig = response.data.data;
          return cachedConfig;
        } else {
          throw new Error(response.data.error || 'Failed to fetch role config');
        }
      } catch (err) {
        console.error('Error fetching role config:', err);
        // Use defaults on error
        cachedConfig = {
          roles: getSortedRoles(),
          roleHierarchy: DEFAULT_ROLE_HIERARCHY,
          roleColors: DEFAULT_ROLE_COLORS,
          elevatedRoles: DEFAULT_ELEVATED_ROLES,
          adminPanelRoles: DEFAULT_ADMIN_PANEL_ROLES,
          userManagementRoles: DEFAULT_USER_MANAGEMENT_ROLES
        };
        throw err;
      } finally {
        cachePromise = null;
      }
    })();

    try {
      const config = await cachePromise;
      setRoleConfig(config);
      setLoading(false);
      return config;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      // Still set the fallback config
      setRoleConfig(cachedConfig);
      return cachedConfig;
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    roleConfig,
    loading,
    error,
    refetch: () => fetchConfig(true),
    // Convenience getters that use the current config
    getRoleColor: (name) => getRoleColor(name),
    getRoleLevel: (name) => getRoleLevel(name),
    isElevatedRole: (name) => isElevatedRole(name),
    getSortedRoles: () => getSortedRoles()
  };
};

// Default export
export default useRoleConfig;

